// ABOUTME: Grows a textarea with its content — CSS field-sizing where available
// ABOUTME: and a scrollHeight measurement everywhere else

import * as React from "react"

// Chrome 123+ / Safari 17.4+ auto-size textareas natively via CSS, which avoids
// the "height: auto" → re-measure dance that causes scroll-anchor jumps on every
// keystroke. Firefox doesn't support it yet and falls back to the JS path.
export const SUPPORTS_FIELD_SIZING =
  typeof window !== "undefined" &&
  typeof CSS !== "undefined" &&
  typeof CSS.supports === "function" &&
  CSS.supports("field-sizing", "content")

/** Style that hands the growing over to the browser, empty on the JS path. */
export const FIELD_SIZING_STYLE = (SUPPORTS_FIELD_SIZING ? { fieldSizing: "content" } : {}) as React.CSSProperties

export const resizeTextArea = (element: HTMLTextAreaElement | null) => {
  if (!element || SUPPORTS_FIELD_SIZING) return

  // Briefly setting height to "auto" lets the textarea shrink to its content,
  // but it also collapses the parent's scrollHeight. Browser scroll anchoring
  // then jumps the nearest scroll container — visible as the field ping-ponging
  // on every keystroke for long values. Preserve and restore scrollTop.
  let scrollParent: HTMLElement | null = element.parentElement
  while (scrollParent) {
    const { overflowY } = window.getComputedStyle(scrollParent)
    if (overflowY === "auto" || overflowY === "scroll") break
    scrollParent = scrollParent.parentElement
  }
  const savedScrollTop = scrollParent?.scrollTop

  element.style.height = "auto"
  element.style.height = `${element.scrollHeight}px`

  if (scrollParent && savedScrollTop !== undefined && scrollParent.scrollTop !== savedScrollTop) {
    scrollParent.scrollTop = savedScrollTop
  }
}

/**
 * Keeps a textarea as tall as its text. Pass the value so the field also grows
 * when the text arrives from outside — a form reset, a server response —
 * and `extraDep` for anything else that changes the box (a min-height prop).
 */
export const useAutoSizeTextArea = (value: unknown, extraDep?: unknown) => {
  const ref = React.useRef<HTMLTextAreaElement | null>(null)

  React.useLayoutEffect(() => {
    resizeTextArea(ref.current)
  }, [value, extraDep])

  return ref
}
